import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { match } from 'ts-pattern';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { Logo } from '@fragment/ui/src/components/Logo/Logo';

import {
  type PersonOnboardingState,
  initialPersonOnboardingState,
  personOnboardingReducer,
} from './personOnboardingReducer';
import { OnboardingCheckBox, OnboardingTextInput } from './SharedUI';

type PersonCDDViewProps = {
  entityId: string;
};

const PersonCDDForm = ({
  onDataSubmitted,
}: {
  onDataSubmitted: () => void;
}) => {
  const [personState, dispatch] = useReducer(
    personOnboardingReducer,
    initialPersonOnboardingState
  );

  const setPersonField = useCallback(
    (stateKey: keyof typeof personState, value: string | boolean) => {
      dispatch({ type: 'SET_FIELD', stateKey, value });
    },
    []
  );

  const setPersonFieldForSharedUI = useCallback(
    (stateKey: string, value: string) => {
      setPersonField(stateKey as keyof typeof personState, value);
    },
    [setPersonField]
  );

  const stateForSharedUI: PersonOnboardingState = personState;
  const isOwner = useMemo(
    () =>
      typeof personState.isOwner === 'boolean' ? personState.isOwner : false,
    [personState.isOwner]
  );

  const handleSubmit = useCallback(() => {
    console.log(personState);
    // SUBMIT DATA TO OSCILAR FOR KYC
    onDataSubmitted();
  }, [personState, onDataSubmitted]);

  const areAllFieldsFilled = useMemo(
    () =>
      Array.from(Object.entries(personState)).every(([key, value]) => {
        if (typeof value === 'boolean') return true;
        if (typeof value === 'string') {
          if (key === 'addressLine2') return true;
          return value.trim() !== '';
        }
        return false;
      }),
    [personState]
  );

  return (
    <div className="flex flex-col h-fit gap-y-f2">
      <span className="text-main">
        The following information is required to complete your onboarding to our
        Payments Platform.
      </span>
      <OnboardingTextInput
        stateKey="name"
        label="Name"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="title"
        label="Title"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />

      <OnboardingTextInput
        stateKey="taxIdSsn"
        label="Tax ID / SSN"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="email"
        label="Email"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="phoneNumber"
        label="Phone Number"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="dateOfBirth"
        label="Date of Birth"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="addressLine1"
        label="Address Line 1"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="addressLine2"
        label="Address Line 2"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="city"
        label="City"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="state"
        label="State"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingTextInput
        stateKey="zipCode"
        label="Zip Code"
        state={stateForSharedUI}
        setField={setPersonFieldForSharedUI}
      />
      <OnboardingCheckBox
        checked={isOwner}
        label="I own more than a 25% stake in the company."
        onClick={() => setPersonField('isOwner', !isOwner)}
      />
      <PrimaryButtonV2
        onClick={handleSubmit}
        disabled={!areAllFieldsFilled}
        tooltipText={
          !areAllFieldsFilled
            ? 'Please fill out all required fields before submitting.'
            : undefined
        }
      >
        Submit
      </PrimaryButtonV2>
    </div>
  );
};

const PersonCDDSubmittedView = () => (
  <div className="flex flex-col h-fit gap-y-f2">
    <span>Your information has been submitted for review.</span>
  </div>
);

type PersonCDDStatus = 'submitted' | 'incomplete';
export const PersonCDDView = ({ entityId }: PersonCDDViewProps) => {
  const [status, setStatus] = useState<PersonCDDStatus>('incomplete');
  const statusView = useMemo(
    () =>
      match<PersonCDDStatus>(status)
        .with('submitted', () => <PersonCDDSubmittedView />)
        .with('incomplete', () => (
          <PersonCDDForm onDataSubmitted={() => setStatus('submitted')} />
        ))
        .exhaustive(),
    [status]
  );
  return (
    <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
      <Logo />
      {statusView}
    </div>
  );
};
